'use client'

import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { InfoCircledIcon } from '@radix-ui/react-icons'
import { useMutation, gql } from '@apollo/client'
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import { ImageUpload } from '@/components/shared/inputComponents/ImageUpload'

const UPDATE_FABRIC_COMBINATION = gql`
  mutation updateExistingFabricCombination(
    $advancedProductId: Float!
    $fabricCombination: FabricCombinationCustomCreateInput
  ) {
    updateExistingFabricCombination(
      advancedProductId: $advancedProductId
      fabricCombination: $fabricCombination
    ) {
      id
    }
  }
`

const formSchema = z.object({
  code: z.string().min(1, 'Code is required'),
  enabled: z.boolean().default(true),
  visible: z.boolean().default(true),
  metadata: z.record(z.any()).default({}),
  'image.src': z.string().optional(),
  profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string(),
        language: z.string(),
      })
    )
    // .transform((items, ctx) => {
    //   const filteredItems = items.filter(
    //     (item) => item.name && item.name?.trim() !== ""
    //   );
    //   if (filteredItems.length === 0) {
    //     items.forEach((item, index) => {
    //       ctx.addIssue({
    //         code: z.ZodIssueCode.custom,
    //         message: "At least one language profile needs to be filled",
    //         path: [`[${index}].name`],
    //       });
    //     });
    //   }
    //   return filteredItems;
    // })
    .refine(profiles => profiles.some(profile => profile.name.length > 1), {
      message:
        'At least one profile must have a name longer than one character',
    }),
})

interface FabricCombinationEditFormProps {
  data: {
    advancedProductId: number
    fabricCombination: {
      id: number
      code: string
      enabled: boolean
      visible: boolean
      metadata: object
      image?: {
        id: number
        src?: string
      } | null
      fabricCombinationProfiles: Array<{
        id: number
        name: string
        description: string | null
        language: string
      }>
      fabricCombinationOptions?: Array<{
        id: number
        code: string
        image?: {
          id: number
          src?: string
        } | null
        fabricCombinationOptionProfiles: Array<{
          id: number
          name: string
          description: string | null
          language: string
        }>
      }>
    }
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function FabricCombinationEditForm({
  data,
  onDirtyChange,
  onRequestClose,
}: FabricCombinationEditFormProps) {
  const { allLanguages, languageId } = useLanguage()
  const [hasManualEnabledValue, setHasManualEnabledValue] = useState(false)
  const isInitialMount = useRef(true)

  const { fabricCombination, advancedProductId } = data
  console.log('FabricCombinationEditForm data', fabricCombination.metadata)

  const handleReset = () => {
    const currentMetadata = form.getValues('metadata') || {}
    if (currentMetadata.manualValues) {
      const { enabled, ...restManualValues } = currentMetadata.manualValues
      const newMetadata = {
        ...currentMetadata,
        manualValues: restManualValues,
      }
      form.setValue('metadata', newMetadata, {
        shouldDirty: true,
      })
    }
  }

  console.log('FabricCombinationEditForm data', fabricCombination)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: fabricCombination.code,
      enabled: fabricCombination.enabled,
      visible: fabricCombination.visible,
      metadata: fabricCombination?.metadata || {},
      'image.src': fabricCombination.image?.src || '',

      profiles: allLanguages.map(lang => {
        const existingProfile =
          fabricCombination?.fabricCombinationProfiles?.find(
            p => p.language === lang
          )
        return {
          id: existingProfile?.id,
          name: existingProfile?.name || '',
          language: lang,
        }
      }),
    },
  })

  const [updateFabricCombination, { loading }] = useMutation(
    UPDATE_FABRIC_COMBINATION,
    {
      onError: e => {
        toast.error('Failed to update fabric combination')
        console.error('Error updating fabric combination:', e)
      },
      onCompleted: () => {
        toast.success('Fabric combination updated successfully')
        // Reset form to mark it as pristine after successful submission
        form.reset(form.getValues())
      },
      refetchQueries: ['GET_PRODUCT_DETAILS'],
    }
  )

  function onSubmit(values: z.infer<typeof formSchema>) {
    updateFabricCombination({
      variables: {
        advancedProductId,
        fabricCombination: {
          id: fabricCombination.id,
          code: values.code,
          enabled: values.enabled,
          visible: values.visible,
          image: values['image.src'] || null,
          fabricCombinationProfiles: values.profiles.map(profile => ({
            id: profile.id,
            name: profile.name,
            // description: profile.description,
            language: profile.language,
          })),
          metadata: values.metadata,
          fabricCombinationOptions:
            fabricCombination.fabricCombinationOptions?.map(option => ({
              id: option.id,
              image: option.image?.src || null,
              fabricCombinationOptionProfiles:
                option.fabricCombinationOptionProfiles.map(profile => ({
                  id: profile.id,
                  name: profile.name,
                  //description: profile.description,
                  language: profile.language,
                })),
            })) || [],
        },
      },
    })
  }

  // Update metadata when enabled changes and differs from server value
  useEffect(() => {
    const currentEnabled = form.watch('enabled')
    const serverEnabled = fabricCombination.enabled || false

    // Skip the effect during initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Only update if the current value differs from server value
    if (currentEnabled !== serverEnabled) {
      const currentMetadata = form.getValues('metadata') || {}

      const newMetadata = {
        ...currentMetadata,
        manualValues: {
          ...(currentMetadata.manualValues || {}),
          enabled: currentEnabled,
        },
      }

      form.setValue('metadata', newMetadata, {
        shouldDirty: true,
      })
    }
  }, [form.watch('enabled'), fabricCombination.enabled])

  // Check if metadata has manualValues.enabled
  useEffect(() => {
    const currentMetadata = form.getValues('metadata') || {}
    setHasManualEnabledValue(
      currentMetadata?.manualValues?.enabled !== undefined
    )
  }, [form.watch('metadata')])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Edit Fabric Combination</SheetTitle>
          <LanguageSwitcher />
        </div>
        <SheetDescription>ID: {fabricCombination.id}</SheetDescription>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Enabled"
                control={form.control}
                name="enabled"
                render={({ field }) => (
                  <FormItem className="p-2">
                    <FormControl>
                      <Switch
                        variant="green"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    {hasManualEnabledValue && (
                      <Tooltip>
                        <TooltipTrigger>
                          <InfoCircledIcon className="h-4 w-4 text-blue-500" />
                        </TooltipTrigger>
                        <TooltipContent className="flex flex-col">
                          <p>
                            This value is not synced with the integration server
                          </p>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-red-500"
                            onClick={e => {
                              e.preventDefault() // Prevent form submission
                              handleReset()
                            }}
                          >
                            Reset
                          </Button>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Visible"
                description="If disabled, the combination will be hidden from the storefront"
                control={form.control}
                name="visible"
                render={({ field }) => (
                  <FormItem className="p-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Image"
                description="Upload combination image"
                control={form.control}
                name="image.src"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ImageUpload
                        field={{
                          ...field,
                          value: field.value ? [field.value] : [],
                          onChange: (urls: string[]) =>
                            field.onChange(urls[0] || ''),
                        }}
                        maxImages={1}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Code"
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter combination code" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Name"
                control={form.control}
                name={`profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter name" {...field} />
                    </FormControl>
                    <FormMessage />
                    {form.formState.errors.profiles?.root && (
                      <p className="text-[0.8rem] font-medium text-destructive">
                        {form.formState.errors.profiles.root.message}
                      </p>
                    )}
                  </FormItem>
                )}
              />

              {fabricCombination.fabricCombinationOptions &&
                fabricCombination.fabricCombinationOptions.length > 0 && (
                  <div className="border p-4 rounded-md">
                    <h3 className="font-medium mb-2">Zones</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {fabricCombination.fabricCombinationOptions.map(
                        option => {
                          const optionProfile =
                            option.fabricCombinationOptionProfiles[0]
                          return (
                            <div
                              key={option.id}
                              className="flex items-center space-x-3 border rounded-md p-2"
                            >
                              {option.image && option.image.src && (
                                <img
                                  src={option.image.src}
                                  alt={
                                    optionProfile?.name ||
                                    `Option ${option.code}`
                                  }
                                  className="w-12 h-12 object-cover rounded-md"
                                />
                              )}
                              <div>
                                <div className="flex items-center space-x-2">
                                  <span className="font-medium">
                                    {optionProfile?.name ||
                                      `Option ${option.code}`}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {option.code}
                                  </span>
                                </div>
                                {optionProfile?.description && (
                                  <p className="text-xs text-gray-500">
                                    {optionProfile.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          )
                        }
                      )}
                    </div>
                  </div>
                )}

              <InputFieldContainer
                label="Metadata (JSON)"
                control={form.control}
                name="metadata"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Enter metadata as JSON"
                        value={JSON.stringify(field.value || {}, null, 2)}
                        onChange={e => {
                          try {
                            const parsed = JSON.parse(e.target.value)
                            field.onChange(parsed)
                          } catch (error) {
                            // If invalid JSON, keep the raw string for editing
                            console.error('Invalid JSON:', error)
                          }
                        }}
                        className="md:text-xs font-mono"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2">
                <Button type="submit" disabled={loading}>
                  Save Changes
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    form.reset()
                    onRequestClose?.()
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
